<script setup>
import { ref, computed, nextTick, onMounted, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import * as ActiveStorage from 'activestorage';
import { DirectUpload } from 'activestorage';
import FileUpload from 'vue-upload-component';
import Button from 'dashboard/components-next/button/Button.vue';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const emit = defineEmits(['send']);

const EmojiIconPicker = defineAsyncComponent(
  () =>
    import('dashboard/components-next/emoji-icon-picker/EmojiIconPicker.vue')
);

const { t } = useI18n();
const { isEditorHotKeyEnabled } = useUISettings();
const cmdEnterKey = useKbd(['$mod', '+', 'enter']);
const enterKey = useKbd(['enter']);

const accountId = useMapGetter('getCurrentAccountId');
const currentUser = useMapGetter('getCurrentUser');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const content = ref('');
const textareaRef = ref(null);
const audioRecorderRef = ref(null);
const uploadRef = ref(null);
const isEmojiPickerOpen = ref(false);
const isRecordingAudio = ref(false);
const recordingAudioState = ref('');
const recordingAudioDurationText = ref('00:00');
const attachedFiles = ref([]);
const isUploading = ref(false);

const showAudioRecorderButton = computed(() =>
  isFeatureEnabledonAccount.value(accountId.value, FEATURE_FLAGS.VOICE_RECORDER)
);

const showAudioPlayStopButton = computed(() => isRecordingAudio.value);

const audioRecorderPlayStopIcon = computed(() => {
  switch (recordingAudioState.value) {
    case 'playing':
      return 'i-ph-pause';
    case 'paused':
    case 'stopped':
      return 'i-ph-play';
    default:
      return 'i-ph-stop';
  }
});

const sendLabel = computed(() => {
  const key = isEditorHotKeyEnabled('cmd_enter')
    ? cmdEnterKey.value
    : enterKey.value;
  return `${t('CONVERSATION.REPLYBOX.SEND')} ${key}`;
});

const isSendDisabled = computed(
  () =>
    (!content.value.trim() && !attachedFiles.value.length) || isUploading.value
);

const adjustHeight = () => {
  if (!textareaRef.value) return;
  textareaRef.value.style.height = 'auto';
  textareaRef.value.style.height = `${Math.min(textareaRef.value.scrollHeight, 200)}px`;
};

const resetRecorder = () => {
  isRecordingAudio.value = false;
  recordingAudioState.value = '';
  recordingAudioDurationText.value = '00:00';
};

const send = () => {
  const text = content.value.trim();
  const signedIds = attachedFiles.value
    .map(f => f.blobSignedId)
    .filter(Boolean);
  if (!text && !signedIds.length) return;
  emit('send', { content: text, signedIds });
  content.value = '';
  attachedFiles.value = [];
  resetRecorder();
  nextTick(adjustHeight);
};

const onKeydown = e => {
  if (e.isComposing) return;
  const isCmdEnter = (e.metaKey || e.ctrlKey) && e.key === 'Enter';
  const isEnterOnly =
    e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey;
  if (isEditorHotKeyEnabled('cmd_enter') && isCmdEnter) {
    e.preventDefault();
    send();
  } else if (isEditorHotKeyEnabled('enter') && isEnterOnly) {
    e.preventDefault();
    send();
  }
};

const insertEmoji = emoji => {
  content.value += emoji;
  isEmojiPickerOpen.value = false;
  nextTick(() => textareaRef.value?.focus());
};

const doDirectUpload = (file, callback) => {
  const upload = new DirectUpload(
    file,
    `/api/v1/accounts/${accountId.value}/agent_conversations/${props.conversationId}/direct_uploads`,
    {
      directUploadWillCreateBlobWithXHR: xhr => {
        xhr.setRequestHeader(
          'api_access_token',
          currentUser.value?.access_token
        );
      },
    }
  );
  upload.create(callback);
};

const pushAttachment = (actualFile, blobSignedId) => {
  const id = `file-${Date.now()}-${Math.random()}`;
  if (actualFile.type?.startsWith('image/')) {
    const reader = new FileReader();
    reader.onloadend = () => {
      attachedFiles.value.push({
        id,
        name: actualFile.name,
        thumb: reader.result,
        blobSignedId,
        type: actualFile.type,
      });
    };
    reader.readAsDataURL(actualFile);
  } else {
    attachedFiles.value.push({
      id,
      name: actualFile.name,
      thumb: null,
      blobSignedId,
      type: actualFile.type,
    });
  }
};

// Called by vue-upload-component: (newFile, oldFile)
// Only process first-time additions (oldFile is undefined)
const onFileUpload = (newFile, oldFile) => {
  if (!newFile || oldFile) return;
  isUploading.value = true;
  doDirectUpload(newFile.file, (error, blob) => {
    if (!error && blob?.signed_id) {
      pushAttachment(newFile.file, blob.signed_id);
    }
    isUploading.value = false;
  });
};

const removeAttachment = id => {
  attachedFiles.value = attachedFiles.value.filter(f => f.id !== id);
};

// ── Audio recorder (mirrors ReplyBox.vue pattern) ──────────────────────────

const toggleAudioRecorder = () => {
  isRecordingAudio.value = !isRecordingAudio.value;
  if (!isRecordingAudio.value) {
    audioRecorderRef.value?.stopRecording();
    recordingAudioState.value = '';
    recordingAudioDurationText.value = '00:00';
    // remove the audio attachment that was queued (user discarded the recording)
    attachedFiles.value = attachedFiles.value.filter(
      f => !f.type?.startsWith('audio/')
    );
  }
};

const toggleAudioRecorderPlayPause = () => {
  if (!audioRecorderRef.value) return;
  if (!recordingAudioState.value) {
    audioRecorderRef.value.stopRecording();
  } else {
    audioRecorderRef.value.playPause();
  }
};

const onRecordProgressChanged = duration => {
  recordingAudioDurationText.value = duration;
};

const onFinishRecorder = ({ file }) => {
  recordingAudioState.value = 'stopped';
  isUploading.value = true;
  doDirectUpload(file, (error, blob) => {
    if (!error && blob?.signed_id) {
      pushAttachment(file, blob.signed_id);
    } else if (error) {
      useAlert(t('CONVERSATION.REPLYBOX.AUDIO_CONVERSION_FAILED'));
      resetRecorder();
    }
    isUploading.value = false;
    // Keep isRecordingAudio = true so user can listen before sending
  });
};

const onRecordError = () => {
  isRecordingAudio.value = false;
  recordingAudioState.value = '';
  useAlert(t('CONVERSATION.REPLYBOX.AUDIO_CONVERSION_FAILED'));
};

onMounted(() => {
  ActiveStorage.start();
  adjustHeight();
});
</script>

<template>
  <div class="relative mb-2 mx-2 border border-n-weak rounded-xl bg-n-solid-1">
    <!-- Emoji picker -->
    <div
      v-if="isEmojiPickerOpen"
      v-on-click-outside="() => (isEmojiPickerOpen = false)"
      class="absolute bottom-full left-0 mb-2 z-50"
    >
      <EmojiIconPicker mode="emoji" @select="insertEmoji($event.value)" />
    </div>

    <!-- Audio recorder (replaces textarea while active) -->
    <div v-if="isRecordingAudio" class="px-3 pt-3">
      <AudioRecorder
        ref="audioRecorderRef"
        audio-record-format="audio/wav"
        @recorder-progress-changed="onRecordProgressChanged"
        @finish-record="onFinishRecorder"
        @record-error="onRecordError"
        @play="recordingAudioState = 'playing'"
        @pause="recordingAudioState = 'paused'"
      />
    </div>

    <!-- textarea: use ! modifier to override global _base.scss field-base rules (unlayered, beat @layer utilities) -->
    <textarea
      v-else
      ref="textareaRef"
      v-model="content"
      class="block w-full resize-none !bg-transparent px-3 pt-3 pb-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 !outline-0 !rounded-none !border-0 !mb-0 overflow-hidden min-h-[90px] max-h-[200px]"
      :placeholder="t('AGENT_CHAT.INPUT_PLACEHOLDER')"
      @input="adjustHeight"
      @keydown="onKeydown"
    />

    <!-- Attachment previews — inside the box, above the toolbar -->
    <div v-if="attachedFiles.length" class="flex flex-wrap gap-2 px-3 pb-2">
      <div
        v-for="file in attachedFiles"
        :key="file.id"
        class="flex items-center gap-1 bg-n-alpha-2 rounded-lg px-2 py-1 text-xs text-n-slate-11"
      >
        <img
          v-if="file.thumb"
          :src="file.thumb"
          class="size-8 object-cover rounded"
          :alt="file.name"
        />
        <span v-else class="i-ph-paperclip size-4 shrink-0" />
        <span class="max-w-[80px] truncate">{{ file.name }}</span>
        <button
          class="ml-1 text-n-slate-9 hover:text-n-slate-12"
          @click="removeAttachment(file.id)"
        >
          <span class="i-lucide-x size-3" />
        </button>
      </div>
    </div>

    <!-- Bottom toolbar — same layout as ReplyBottomPanel.vue -->
    <div class="flex justify-between p-3">
      <div class="flex items-center gap-2">
        <Button
          v-tooltip.top-end="t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
          icon="i-ph-smiley-sticker"
          slate
          faded
          sm
          @click="isEmojiPickerOpen = !isEmojiPickerOpen"
        />

        <FileUpload
          ref="uploadRef"
          input-id="agentChatAttachment"
          :size="4096 * 4096"
          multiple
          drop
          :drop-directory="false"
          :data="{
            direct_upload_url: `/api/v1/accounts/${accountId}/agent_conversations/${conversationId}/direct_uploads`,
            direct_upload: true,
          }"
          @input-file="onFileUpload"
        >
          <Button
            v-tooltip.top-end="t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            icon="i-ph-paperclip"
            slate
            faded
            sm
          />
        </FileUpload>

        <Button
          v-if="showAudioRecorderButton"
          v-tooltip.top-end="t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
          :icon="isRecordingAudio ? 'i-ph-microphone-slash' : 'i-ph-microphone'"
          slate
          faded
          sm
          @click="toggleAudioRecorder"
        />

        <!-- Play/stop button + timer shown while recorder is active -->
        <Button
          v-if="showAudioPlayStopButton"
          :icon="audioRecorderPlayStopIcon"
          :label="recordingAudioDurationText"
          slate
          faded
          sm
          @click="toggleAudioRecorderPlayPause"
        />
      </div>

      <Button
        :label="sendLabel"
        type="submit"
        sm
        blue
        :disabled="isSendDisabled"
        @click="send"
      />
    </div>

    <!-- Drag-and-drop overlay -->
    <transition name="modal-fade">
      <div
        v-show="uploadRef && uploadRef.dropActive"
        class="flex fixed inset-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
      >
        <span class="i-lucide-cloud-upload size-10" />
        <h4 class="text-2xl break-words text-n-slate-12">
          {{ t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
        </h4>
      </div>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
:deep(.file-uploads) {
  label {
    @apply cursor-pointer;
  }
  &:hover button {
    @apply enabled:bg-n-slate-9/20;
  }
}
</style>
